/**
 * Goal taxonomy for the template experience redesign (Phase 1).
 *
 * See docs/development/template-experience-redesign-plan.md §3.1–3.2.
 * Owners pick templates by "what they want to achieve" (goal) rather than
 * by industry alone. Industry becomes the content-profile input; goal
 * becomes the structure/CTA-flow input.
 *
 * This module intentionally has no dependency on `template-catalog.ts` to
 * avoid a circular import — `template-catalog.ts` may import from here.
 */

import type { IndustryId } from './industry';

export const GOAL_KEYS = ['booking', 'discovery', 'trust', 'repeat', 'sell', 'recruit'] as const;
export type GoalKey = (typeof GOAL_KEYS)[number];

export type GoalDefinition = {
  readonly key: GoalKey;
  /** Owner-facing label — the wording an owner would use, not industry jargon. */
  readonly label: string;
  /** One-line summary shown on the goal-selection card. */
  readonly tagline: string;
};

export const GOAL_DEFINITIONS: readonly GoalDefinition[] = Object.freeze([
  {
    key: 'booking',
    label: '予約・来店を増やしたい',
    tagline: 'メニューを見たお客様が、そのまま予約まで進める導線をつくります',
  },
  {
    key: 'discovery',
    label: '新しいお客様に見つけてもらいたい',
    tagline: '検索や地図からの来訪者に、営業時間・場所・雰囲気がすぐ伝わるサイトにします',
  },
  {
    key: 'trust',
    label: '信頼されて相談・依頼につなげたい',
    tagline: '実績とプロフィールで安心してもらい、問い合わせの一歩を後押しします',
  },
  {
    key: 'repeat',
    label: '常連・ファンとつながりたい',
    tagline: 'お知らせや新メニューを1分で更新して、また来たくなる理由を届け続けます',
  },
  {
    key: 'sell',
    label: '商品・テイクアウトを売りたい',
    tagline: 'おすすめ商品を並べて、注文・購入への道をまっすぐにします',
  },
  {
    key: 'recruit',
    label: '採用を強化したい',
    tagline: '働く人と社風を見せて、応募したくなる会社の顔をつくります',
  },
]);

export const GOAL_BY_KEY: ReadonlyMap<GoalKey, GoalDefinition> = new Map(
  GOAL_DEFINITIONS.map((g) => [g.key, g]),
);

export function isGoalKey(value: unknown): value is GoalKey {
  return typeof value === 'string' && (GOAL_KEYS as readonly string[]).includes(value);
}

export function getGoalDefinition(key: GoalKey): GoalDefinition {
  const def = GOAL_BY_KEY.get(key);
  if (!def) {
    throw new Error(`Unknown goal key: ${key}`);
  }
  return def;
}

/**
 * Industry vocabulary aligned with the existing 6 template theme slugs
 * (`template-catalog.ts` categories / `apps/builder/themes/`).
 *
 * Aliased to the signup-flow `IndustryId` (industry.ts) — the two taxonomies
 * share identical values and must never drift.
 */
export type IndustryKey = IndustryId;
export const INDUSTRY_KEYS = [
  'beauty',
  'restaurant',
  'law',
  'corporate',
  'retail',
  'medical',
] as const satisfies readonly IndustryKey[];

// Compile-time completeness check: INDUSTRY_KEYS must cover every IndustryId.
type MissingIndustry = Exclude<IndustryId, (typeof INDUSTRY_KEYS)[number]>;
const _assertIndustryKeysComplete: MissingIndustry extends never ? true : never = true;
void _assertIndustryKeysComplete;

export function isIndustryKey(value: unknown): value is IndustryKey {
  return typeof value === 'string' && (INDUSTRY_KEYS as readonly string[]).includes(value);
}

/**
 * Maps the existing Japanese category labels used in `TEMPLATE_CATALOG`
 * (`TemplateEntry.category`) to the new `IndustryKey` vocabulary.
 */
export const CATEGORY_TO_INDUSTRY: Record<string, IndustryKey> = Object.freeze({
  '美容室・サロン': 'beauty',
  '飲食・カフェ': 'restaurant',
  '士業・コンサル': 'law',
  '会社・コーポレート': 'corporate',
  '小売・ショップ': 'retail',
  'クリニック・医療': 'medical',
});

/** Reverse lookup: IndustryKey -> Japanese category label. */
export const INDUSTRY_LABELS: Record<IndustryKey, string> = Object.freeze({
  beauty: '美容室・サロン',
  restaurant: '飲食・カフェ',
  law: '士業・コンサル',
  corporate: '会社・コーポレート',
  retail: '小売・ショップ',
  medical: 'クリニック・医療',
});

/**
 * Which goals are offered per industry. Industry × goal combinations not
 * listed here are not surfaced in the catalog/picker (per plan §3.1: "業種
 * ごとに適合するゴール 4〜6 種を提供").
 */
export const GOAL_INDUSTRY_FIT: Record<IndustryKey, readonly GoalKey[]> = Object.freeze({
  beauty: ['booking', 'discovery', 'repeat', 'trust'],
  restaurant: ['booking', 'discovery', 'repeat', 'sell'],
  law: ['trust', 'discovery', 'booking'],
  corporate: ['trust', 'recruit', 'discovery', 'sell'],
  retail: ['sell', 'discovery', 'repeat', 'trust'],
  medical: ['booking', 'discovery', 'trust', 'recruit'],
});

export function isGoalFitForIndustry(goal: GoalKey, industry: IndustryKey): boolean {
  return GOAL_INDUSTRY_FIT[industry].includes(goal);
}
